import { useState, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { FileDown, Loader2, BarChart3 } from 'lucide-react';
import { toast } from '@/components/ui/sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  LineChart, Line,
} from 'recharts';

const fmt = (v) => {
  if (v === undefined || v === null || isNaN(v)) return '—';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(v);
};
const pct = (v) => v !== undefined && v !== null ? `${(v * 100).toFixed(1)}%` : '—';

export default function ReportGeneratorPage({ analysis, results, data }) {
  const [generating, setGenerating] = useState(false);
  const [selectedSections, setSelectedSections] = useState({
    cover: true,
    dealComparison: true,
    sde: true,
    pnl: true,
    solvency: true,
    workingCapital: true,
    dcf: true,
    scenarios: true,
    sensitivity: true,
    compareDeals: true,
    findings: true,
  });
  
  const chartRevRef = useRef(null);
  const chartMarginRef = useRef(null);
  const chartScenarioRef = useRef(null);
  const chartTornadoRef = useRef(null);
  const chartHistogramRef = useRef(null);

  const dc = results?.deal_comparison || {};
  const sde = results?.sde_analysis || [];
  const pnl = results?.historical_pnl || [];
  const solvency = results?.solvency || {};
  const inventory = results?.inventory || [];
  const workingCapital = results?.working_capital || {};
  const dcf = results?.dcf || {};
  const scenarios = results?.scenarios || {};
  const sensitivity = results?.sensitivity || {};
  const monteCarlo = results?.monte_carlo || {};
  const manualComps = data?.manual_comps || [];
  const flags = results?.trend_flags || [];
  const insights = analysis?.ai_insights?.insights || '';

  // Section configuration with data check
  const sectionConfig = [
    { key: 'cover', label: 'Cover Page & Table of Contents', ready: true },
    { key: 'dealComparison', label: 'Deal Comparison Table', ready: !!dc.seller },
    { key: 'sde', label: 'SDE Analysis', ready: sde.length > 0 },
    { key: 'pnl', label: 'Historical P&L + Embedded Charts', ready: pnl.length > 0 },
    { key: 'solvency', label: 'Solvency Ratios + Inventory', ready: !!solvency.seller || inventory.length > 0 },
    { key: 'workingCapital', label: 'Working Capital Analysis', ready: !!workingCapital.ttm_avg_nwc || workingCapital.monthly_nwc?.length > 0 },
    { key: 'dcf', label: 'DCF Valuation + Cash Flow Waterfall + ROI Metrics', ready: !!dcf.enterprise_value },
    { key: 'scenarios', label: 'Scenario Analysis + Chart + Yearly Projections', ready: Object.keys(scenarios).length > 0 },
    { key: 'sensitivity', label: 'Sensitivity (Tornado) + Monte Carlo (Histogram)', ready: !!sensitivity.base_ev || sensitivity.variables?.length > 0 || !!monteCarlo.mean },
    { key: 'compareDeals', label: 'Compare Deals (Manual Comps)', ready: manualComps.length > 0 },
    { key: 'findings', label: 'Key Findings, Red Flags & AI Analysis', ready: flags.length > 0 || !!insights },
  ];

  const toggleSection = (key) => {
    setSelectedSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAll = () => {
    const allSelected = {};
    sectionConfig.forEach(s => { allSelected[s.key] = true; });
    setSelectedSections(allSelected);
  };

  const selectNone = () => {
    setSelectedSections({ cover: true }); // Always keep cover
  };

  // Chart data
  const revenueChartData = pnl.map(p => ({
    year: p.year,
    Revenue: p.revenue,
    'Gross Profit': p.gross_profit,
  }));
  const marginChartData = pnl.map(p => ({
    year: p.year,
    'Gross Margin': p.gross_margin,
    'Op. Margin': p.operating_margin,
  }));
  const scenarioChartData = Object.entries(scenarios).map(([name, s]) => ({
    name: name.length > 18 ? name.substring(0, 16) + '...' : name,
    'Enterprise Value': s.enterprise_value,
    'Asking Price': dc.seller?.purchase_price || 449000,
  }));

  const captureChart = async (ref) => {
    if (!ref.current) return null;
    try {
      const html2canvas = (await import('html2canvas')).default;
      const canvas = await html2canvas(ref.current, {
        backgroundColor: '#09090b',
        scale: 2,
        useCORS: true,
        logging: false,
      });
      return canvas.toDataURL('image/png', 0.8);
    } catch (e) {
      console.error('Chart capture error:', e);
      return null;
    }
  };

  const generatePDF = useCallback(async () => {
    setGenerating(true);
    try {
      // Capture charts
      const [revImg, marginImg, scenarioImg, tornadoImg, histogramImg] = await Promise.all([
        selectedSections.pnl ? captureChart(chartRevRef) : null,
        selectedSections.pnl ? captureChart(chartMarginRef) : null,
        selectedSections.scenarios ? captureChart(chartScenarioRef) : null,
        selectedSections.sensitivity ? captureChart(chartTornadoRef) : null,
        selectedSections.sensitivity ? captureChart(chartHistogramRef) : null,
      ]);

      const jsPDFModule = await import('jspdf');
      const jsPDF = jsPDFModule.default;
      const autoTableModule = await import('jspdf-autotable');
      const autoTable = autoTableModule.default;

      const doc = new jsPDF('p', 'mm', 'a4');
      const pageW = doc.internal.pageSize.getWidth();
      const pageH = doc.internal.pageSize.getHeight();
      let y = 20;
      let lastTableY = 20;
      let currentSectionNum = 0;

      // FIXED: fillBg now called on every new page
      const fillBg = () => { 
        doc.setFillColor(9, 9, 11); 
        doc.rect(0, 0, pageW, pageH, 'F'); 
      };
      
      const addPage = () => { 
        doc.addPage(); 
        fillBg(); // FIXED: Always fill background on new page
        y = 20; 
      };
      
      // FIXED: checkSpace now fills background when adding new page
      const checkSpace = (needed) => { 
        if (y + needed > pageH - 20) {
          addPage();
        }
      };

      // Build dynamic table of contents based on selected sections
      const tocSections = [];
      if (selectedSections.dealComparison && dc.seller) tocSections.push('Deal Comparison');
      if (selectedSections.sde && sde.length > 0) tocSections.push('SDE Analysis');
      if (selectedSections.pnl && pnl.length > 0) tocSections.push('Historical P&L (with Charts)');
      if (selectedSections.solvency && (solvency.seller || inventory.length > 0)) tocSections.push('Solvency Ratios & Inventory');
      if (selectedSections.workingCapital && (workingCapital.ttm_avg_nwc || workingCapital.monthly_nwc?.length > 0)) tocSections.push('Working Capital Analysis');
      if (selectedSections.dcf && dcf.enterprise_value) tocSections.push('DCF Valuation');
      if (selectedSections.scenarios && Object.keys(scenarios).length > 0) tocSections.push('Scenario Analysis (with Charts)');
      if (selectedSections.sensitivity && (sensitivity.base_ev || sensitivity.variables?.length > 0 || monteCarlo.mean)) tocSections.push('Sensitivity & Monte Carlo');
      if (selectedSections.compareDeals && manualComps.length > 0) tocSections.push('Comparable Deals');
      if (selectedSections.findings && (flags.length > 0 || insights)) tocSections.push('Key Findings & AI Analysis');

      // ── Cover Page ──
      if (selectedSections.cover) {
        fillBg();
        doc.setFillColor(99, 102, 241);
        doc.rect(15, 40, 3, 40, 'F');

        doc.setTextColor(250, 250, 250);
        doc.setFontSize(28);
        doc.text('Acquisition Analysis', 25, 55);
        doc.setFontSize(14);
        doc.setTextColor(161, 161, 170);
        doc.text(analysis?.name || 'Business Analysis', 25, 68);
        doc.setFontSize(10);
        doc.text(`Generated: ${new Date().toLocaleDateString()}`, 25, 78);

        // Table of contents
        y = 100;
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(12);
        doc.text('Contents', 25, y); y += 8;
        doc.setFontSize(9);
        doc.setTextColor(161, 161, 170);
        tocSections.forEach((s, i) => {
          doc.text(`${i + 1}. ${s}`, 30, y);
          y += 5;
        });

        doc.setTextColor(99, 102, 241);
        doc.setFontSize(10);
        doc.text('AcquiFlow', 25, pageH - 20);
        doc.setTextColor(113, 113, 122);
        doc.text('Business Acquisition Analysis Tool', 55, pageH - 20);
      }

      // ── Deal Comparison ──
      if (selectedSections.dealComparison && dc.seller) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Deal Comparison', 15, y + 6);
        y += 14;

        const dealRows = [
          ['Purchase Price', fmt(dc.seller?.purchase_price), fmt(dc.buyer?.purchase_price), fmt(dc.comparable?.purchase_price)],
          ['Total Deal Size', fmt(dc.seller?.total_deal_size), fmt(dc.buyer?.total_deal_size), fmt(dc.comparable?.total_deal_size)],
          ['SDE Multiple', (dc.seller?.sde_multiple?.toFixed(2) || '—') + 'x', (dc.buyer?.sde_multiple?.toFixed(2) || '—') + 'x', (dc.comparable?.sde_multiple?.toFixed(2) || '—') + 'x'],
          ['Total Debt Service', fmt(dc.seller?.total_debt_service), fmt(dc.buyer?.total_debt_service), fmt(dc.comparable?.total_debt_service)],
          ['DSCR', (dc.seller?.dscr?.toFixed(2) || '—') + 'x', (dc.buyer?.dscr?.toFixed(2) || '—') + 'x', (dc.comparable?.dscr?.toFixed(2) || '—') + 'x'],
          ['FCF to Buyer', fmt(dc.seller?.fcf_to_buyer), fmt(dc.buyer?.fcf_to_buyer), fmt(dc.comparable?.fcf_to_buyer)],
          ['SBA Loan', fmt(dc.seller?.sba_loan), fmt(dc.buyer?.sba_loan), fmt(dc.comparable?.sba_loan)],
          ['Seller Note', fmt(dc.seller?.seller_note), fmt(dc.buyer?.seller_note), fmt(dc.comparable?.seller_note)],
          ['Equity Injection', fmt(dc.seller?.equity_injection), fmt(dc.buyer?.equity_injection), fmt(dc.comparable?.equity_injection)],
        ];

        autoTable(doc, {
          startY: y,
          head: [['Metric', 'Seller', 'Buyer', 'Comparable']],
          body: dealRows,
          theme: 'plain',
          styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { 
            lastTableY = data.cursor.y;
            // Fill background on new pages created by autoTable
            if (data.pageNumber > 1) {
              doc.setFillColor(9, 9, 11);
              doc.rect(0, 0, pageW, pageH, 'F');
            }
          }
        });
        y = lastTableY + 8;
      }

      // ── SDE ──
      if (selectedSections.sde && sde.length > 0) {
        checkSpace(50);
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('SDE Analysis', 15, y + 6);
        y += 14;

        const sdeRows = sde.map(s => [
          s.label, fmt(s.net_income), fmt(s.officer_salary), fmt(s.depreciation), fmt(s.entertainment), fmt(s.other_addbacks), fmt(s.sde)
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Period', 'Net Income', 'Officer Salary', 'Depreciation', 'Entertainment', 'Other', 'SDE']],
          body: sdeRows,
          theme: 'plain',
          styles: { fontSize: 7.5, textColor: [200, 200, 200], cellPadding: 2.5 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { lastTableY = data.cursor.y; }
        });
        y = lastTableY + 8;
      }

      // ── P&L with Charts ──
      if (selectedSections.pnl && pnl.length > 0) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Historical P&L Summary', 15, y + 6);
        y += 14;

        // Embed revenue chart image
        if (revImg) {
          doc.addImage(revImg, 'PNG', 15, y, pageW - 30, 55);
          y += 58;
        }

        // Embed margin chart image
        if (marginImg) {
          checkSpace(60);
          doc.addImage(marginImg, 'PNG', 15, y, pageW - 30, 45);
          y += 48;
        }

        checkSpace(50);
        const pnlRows = pnl.map(p => [
          p.year, fmt(p.revenue), pct(p.gross_margin), fmt(p.operating_income), pct(p.operating_margin), p.yoy_growth !== null ? pct(p.yoy_growth) : '—'
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Year', 'Revenue', 'GM%', 'Op. Income', 'Op. Margin', 'YoY Growth']],
          body: pnlRows,
          theme: 'plain',
          styles: { fontSize: 7.5, textColor: [200, 200, 200], cellPadding: 2.5 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { lastTableY = data.cursor.y; }
        });
        y = lastTableY + 8;
      }

      // ── Solvency ──
      if (selectedSections.solvency && (solvency.seller || solvency.buyer)) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Solvency Ratios', 15, y + 6);
        y += 14;

        const solRows = [
          ['Current Ratio', (solvency.seller?.current_ratio?.toFixed(2) || '—') + 'x', (solvency.buyer?.current_ratio?.toFixed(2) || '—') + 'x', '> 1.5x'],
          ['Debt-to-Equity', (solvency.seller?.debt_to_equity?.toFixed(2) || '—') + 'x', (solvency.buyer?.debt_to_equity?.toFixed(2) || '—') + 'x', '< 2.0x'],
          ['Solvency Ratio', (solvency.seller?.solvency_ratio?.toFixed(2) || '—') + 'x', (solvency.buyer?.solvency_ratio?.toFixed(2) || '—') + 'x', '> 1.0x'],
          ['DSCR', (solvency.seller?.dscr?.toFixed(2) || '—') + 'x', (solvency.buyer?.dscr?.toFixed(2) || '—') + 'x', '> 1.35x'],
        ];

        autoTable(doc, {
          startY: y,
          head: [['Ratio', 'Seller', 'Buyer', 'Benchmark']],
          body: solRows,
          theme: 'plain',
          styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { lastTableY = data.cursor.y; }
        });
        y = lastTableY + 8;

        // Inventory
        if (inventory.length > 0) {
          checkSpace(40);
          doc.setFontSize(12);
          doc.setTextColor(250, 250, 250);
          doc.text('Inventory Turnover', 15, y); y += 6;

          const invRows = inventory.map(i => [
            i.label, fmt(i.cogs), fmt(i.inventory), (i.turnover?.toFixed(2) || '—') + 'x', (i.days_outstanding?.toFixed(1) || '—') + ' days'
          ]);

          autoTable(doc, {
            startY: y,
            head: [['Scenario', 'COGS', 'Inventory', 'Turnover', 'Days Outstanding']],
            body: invRows,
            theme: 'plain',
            styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
            headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [24, 24, 27] },
            margin: { left: 15, right: 15 },
            didDrawPage: (data) => { lastTableY = data.cursor.y; }
          });
          y = lastTableY + 8;
        }
      }

      // ── Working Capital ──
      if (selectedSections.workingCapital && (workingCapital.ttm_avg_nwc || workingCapital.monthly_nwc?.length > 0)) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Working Capital Analysis', 15, y + 6);
        y += 14;

        // Summary box
        doc.setFillColor(24, 24, 27);
        doc.roundedRect(15, y, pageW - 30, 28, 3, 3, 'F');
        doc.setFontSize(10);
        doc.setTextColor(99, 102, 241);
        doc.text('Working Capital Summary', 20, y + 7);
        doc.setFontSize(9);
        doc.setTextColor(200, 200, 200);
        doc.text(`TTM Average NWC: ${fmt(workingCapital.ttm_avg_nwc || 0)}`, 20, y + 14);
        doc.text(`NWC Peg: ${fmt(workingCapital.peg || 0)}`, 20, y + 20);
        doc.text(`Collar Range: ${fmt(workingCapital.collar_low || 0)} - ${fmt(workingCapital.collar_high || 0)}`, 100, y + 14);
        doc.text(`True-Up Threshold: ±${fmt(workingCapital.collar_pct ? workingCapital.collar_pct * (workingCapital.peg || 0) : 0)}`, 100, y + 20);
        y += 34;

        // Monthly NWC data if available
        if (workingCapital.monthly_nwc?.length > 0) {
          checkSpace(50);
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Monthly Net Working Capital (TTM)', 15, y); y += 6;

          const wcRows = workingCapital.monthly_nwc.map(m => [
            m.month, fmt(m.total_ca), fmt(m.total_cl), fmt(m.nwc)
          ]);

          autoTable(doc, {
            startY: y,
            head: [['Month', 'Current Assets', 'Current Liabilities', 'NWC']],
            body: wcRows,
            theme: 'plain',
            styles: { fontSize: 7.5, textColor: [200, 200, 200], cellPadding: 2.5 },
            headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [24, 24, 27] },
            margin: { left: 15, right: 15 },
            didDrawPage: (data) => { lastTableY = data.cursor.y; }
          });
          y = lastTableY + 8;
        }
      }

      // ── DCF ──
      if (selectedSections.dcf && dcf.enterprise_value) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('DCF Valuation', 15, y + 6);
        y += 14;

        // Summary box
        doc.setFillColor(24, 24, 27);
        doc.roundedRect(15, y, pageW - 30, 28, 3, 3, 'F');
        doc.setFontSize(10);
        doc.setTextColor(99, 102, 241);
        doc.text(`Enterprise Value: ${fmt(dcf.enterprise_value)}`, 20, y + 8);
        doc.setTextColor(161, 161, 170);
        doc.text(`Implied Multiple: ${dcf.implied_multiple?.toFixed(2)}x  |  PV FCFs: ${fmt(dcf.sum_pv_fcf)}  |  PV Terminal: ${fmt(dcf.pv_terminal)}`, 20, y + 16);
        const exitMult = data?.dcf_assumptions?.exit_multiple || 3.0;
        doc.text(`Exit Multiple: ${exitMult}x  |  Terminal Value: ${fmt(dcf.terminal_value)}`, 20, y + 22);
        y += 34;

        if (dcf.projections?.length) {
          const dcfRows = dcf.projections.map(p => [
            `Year ${p.year}`, pct(p.growth_rate), fmt(p.revenue), fmt(p.capex || 0), fmt(p.nwc_change || 0), fmt(p.fcf), fmt(p.pv_fcf)
          ]);

          autoTable(doc, {
            startY: y,
            head: [['Year', 'Growth', 'Revenue', 'CapEx', 'NWC Chg', 'FCF', 'PV FCF']],
            body: dcfRows,
            theme: 'plain',
            styles: { fontSize: 7.5, textColor: [200, 200, 200], cellPadding: 2.5 },
            headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
            alternateRowStyles: { fillColor: [24, 24, 27] },
            margin: { left: 15, right: 15 },
            didDrawPage: (data) => { lastTableY = data.cursor.y; }
          });
          y = lastTableY + 8;
        }

        // ── Buyer Cash Flow Waterfall ──
        const buyerDeal = dc.buyer || {};
        const sbaDebtService = buyerDeal.sba_debt_service || 0;
        const sellerDebtService = buyerDeal.seller_debt_service || 0;
        const sellerNoteYears = data?.buyer_deal?.seller_note_years || 5;
        const sbaYears = data?.buyer_deal?.sba_years || 10;
        const equityInjection = buyerDeal.equity_injection || 0;
        const buyerSalary = data?.dcf_assumptions?.buyer_salary || buyerDeal.buyer_salary || 75000;

        if (dcf.projections?.length > 0 && equityInjection > 0) {
          checkSpace(70);
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Buyer Cash Flow Waterfall', 15, y);
          y += 6;

          const waterfallRows = [];
          let cumulativeCash = 0;

          for (let i = 0; i < 5; i++) {
            const yearNum = i + 1;
            const fcf = dcf.projections[i]?.fcf || 0;
            const sbaPayment = yearNum <= sbaYears ? sbaDebtService : 0;
            const sellerPayment = yearNum <= sellerNoteYears ? sellerDebtService : 0;
            const cashAfterDebt = fcf - sbaPayment - sellerPayment;
            const totalCash = cashAfterDebt + buyerSalary;
            cumulativeCash += totalCash;

            waterfallRows.push([
              `Year ${yearNum}`,
              fmt(fcf),
              fmt(-sbaPayment),
              fmt(-sellerPayment),
              fmt(cashAfterDebt),
              fmt(buyerSalary),
              fmt(totalCash),
              fmt(cumulativeCash)
            ]);
          }

          autoTable(doc, {
            startY: y,
            head: [['Year', 'FCF', 'SBA Pmt', 'Seller Pmt', 'Cash After Debt', 'Salary', 'Total Cash', 'Cumulative']],
            body: waterfallRows,
            theme: 'plain',
            styles: { fontSize: 6.5, textColor: [200, 200, 200], cellPadding: 2 },
            headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold', fontSize: 6 },
            alternateRowStyles: { fillColor: [24, 24, 27] },
            margin: { left: 15, right: 15 },
            didDrawPage: (data) => { lastTableY = data.cursor.y; }
          });
          y = lastTableY + 8;

          // ── Buyer ROI Metrics ──
          checkSpace(35);
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Buyer ROI Metrics', 15, y);
          y += 6;

          const year1TotalCash = waterfallRows[0] ? parseFloat(waterfallRows[0][6].replace(/[$,]/g, '')) || 0 : 0;
          const cashOnCash = equityInjection > 0 ? (year1TotalCash / equityInjection) * 100 : 0;
          const fiveYearReturn = equityInjection > 0 ? cumulativeCash / equityInjection : 0;
          const paybackPeriod = year1TotalCash > 0 ? equityInjection / year1TotalCash : 0;

          const getRating = (metric, value) => {
            if (metric === 'coc') return value >= 25 ? 'Strong' : value >= 15 ? 'Moderate' : 'Weak';
            if (metric === '5yr') return value >= 2.5 ? 'Strong' : value >= 1.5 ? 'Moderate' : 'Weak';
            if (metric === 'payback') return value <= 2 ? 'Strong' : value <= 4 ? 'Moderate' : 'Weak';
            return '—';
          };

          doc.setFillColor(24, 24, 27);
          doc.roundedRect(15, y, pageW - 30, 24, 3, 3, 'F');
          doc.setFontSize(9);
          doc.setTextColor(200, 200, 200);
          doc.text(`Cash-on-Cash Return (Year 1): ${cashOnCash.toFixed(1)}%`, 20, y + 7);
          doc.text(`Rating: ${getRating('coc', cashOnCash)}`, 100, y + 7);
          doc.text(`5-Year Total Return: ${fiveYearReturn.toFixed(2)}x`, 20, y + 14);
          doc.text(`Rating: ${getRating('5yr', fiveYearReturn)}`, 100, y + 14);
          doc.text(`Simple Payback Period: ${paybackPeriod.toFixed(1)} years`, 20, y + 21);
          doc.text(`Rating: ${getRating('payback', paybackPeriod)}`, 100, y + 21);
          y += 30;
        }
      }

      // ── Scenarios with Chart ──
      if (selectedSections.scenarios && Object.keys(scenarios).length > 0) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Scenario Analysis', 15, y + 6);
        y += 14;

        // Embed scenario chart
        if (scenarioImg) {
          checkSpace(65);
          doc.addImage(scenarioImg, 'PNG', 15, y, pageW - 30, 55);
          y += 58;
        }

        // Scenario Comparison Summary Table
        checkSpace(50);
        doc.setFontSize(11);
        doc.setTextColor(250, 250, 250);
        doc.text('Scenario Comparison Summary', 15, y);
        y += 6;

        const askingPrice = dc.seller?.purchase_price || 449000;
        const scenarioRows = Object.entries(scenarios).map(([name, s]) => [
          name, 
          fmt(s.enterprise_value), 
          fmt(s.enterprise_value - askingPrice),
          (s.implied_multiple?.toFixed(2) || '—') + 'x', 
          fmt(s.terminal_value || 0),
          s.enterprise_value > askingPrice ? 'Supports' : 'Below'
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Scenario', 'Enterprise Value', 'vs Asking', 'Multiple', 'Terminal Value', 'Assessment']],
          body: scenarioRows,
          theme: 'plain',
          styles: { fontSize: 7.5, textColor: [200, 200, 200], cellPadding: 2.5 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { lastTableY = data.cursor.y; }
        });
        y = lastTableY + 10;

        // Detailed Yearly Projections per Scenario
        Object.entries(scenarios).forEach(([name, s]) => {
          if (s.projections?.length > 0) {
            checkSpace(60);
            doc.setFontSize(10);
            doc.setTextColor(99, 102, 241);
            doc.text(`${name} - Yearly Projections`, 15, y);
            y += 5;

            const projRows = s.projections.map(p => [
              `Year ${p.year}`,
              pct(p.growth_rate),
              fmt(p.revenue),
              fmt(p.fcf),
              fmt(p.nwc_change || 0),
              fmt(p.pv_fcf)
            ]);

            autoTable(doc, {
              startY: y,
              head: [['Year', 'Growth', 'Revenue', 'FCF', 'NWC Change', 'PV FCF']],
              body: projRows,
              theme: 'plain',
              styles: { fontSize: 7, textColor: [200, 200, 200], cellPadding: 2 },
              headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold', fontSize: 7 },
              alternateRowStyles: { fillColor: [24, 24, 27] },
              margin: { left: 15, right: 15 },
              didDrawPage: (data) => { lastTableY = data.cursor.y; }
            });
            y = lastTableY + 6;
          }
        });
      }

      // ── Sensitivity Analysis ──
      if (selectedSections.sensitivity && (sensitivity.base_ev || sensitivity.variables?.length > 0 || monteCarlo.mean)) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Sensitivity & Monte Carlo Analysis', 15, y + 6);
        y += 14;

        // Summary box
        doc.setFillColor(24, 24, 27);
        doc.roundedRect(15, y, pageW - 30, 16, 3, 3, 'F');
        doc.setFontSize(10);
        doc.setTextColor(99, 102, 241);
        doc.text(`Base Enterprise Value: ${fmt(sensitivity.base_ev || dcf.enterprise_value || 0)}`, 20, y + 10);
        y += 22;

        // Tornado Chart Image
        if (tornadoImg) {
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Tornado Chart - Variable Impact on EV', 15, y);
          y += 4;
          doc.addImage(tornadoImg, 'PNG', 15, y, pageW - 30, 50);
          y += 54;
        }

        // Variable Impact Details Table
        if (sensitivity.variables?.length > 0) {
          checkSpace(60);
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Variable Impact Details', 15, y);
          y += 6;

          const sensRows = sensitivity.variables.map(v => [
            v.variable,
            `${((v.low_value || 0) * 100).toFixed(1)}%`,
            `${((v.base_value || 0) * 100).toFixed(1)}%`,
            `${((v.high_value || 0) * 100).toFixed(1)}%`,
            fmt(v.ev_low),
            fmt(v.ev_high),
            fmt(v.spread)
          ]);

          autoTable(doc, {
            startY: y,
            head: [['Variable', 'Low', 'Base', 'High', 'EV (Low)', 'EV (High)', 'Spread']],
            body: sensRows,
            theme: 'plain',
            styles: { fontSize: 7, textColor: [200, 200, 200], cellPadding: 2.5 },
            headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold', fontSize: 7 },
            alternateRowStyles: { fillColor: [24, 24, 27] },
            margin: { left: 15, right: 15 },
            didDrawPage: (data) => { lastTableY = data.cursor.y; }
          });
          y = lastTableY + 10;
        }

        // Monte Carlo results
        if (monteCarlo.mean) {
          checkSpace(80);
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Monte Carlo Simulation', 15, y);
          y += 6;

          // Monte Carlo summary box
          doc.setFillColor(24, 24, 27);
          doc.roundedRect(15, y, pageW - 30, 28, 3, 3, 'F');
          doc.setFontSize(9);
          doc.setTextColor(200, 200, 200);
          doc.text(`Simulations: ${monteCarlo.n_simulations?.toLocaleString() || 'N/A'}`, 20, y + 7);
          doc.text(`Mean EV: ${fmt(monteCarlo.mean)}`, 20, y + 14);
          doc.text(`Std Dev: ${fmt(monteCarlo.std)}`, 20, y + 21);
          doc.text(`Median: ${fmt(monteCarlo.percentiles?.p50)}`, 100, y + 7);
          doc.text(`Min: ${fmt(monteCarlo.min)}`, 100, y + 14);
          doc.text(`Max: ${fmt(monteCarlo.max)}`, 100, y + 21);
          y += 34;

          // Monte Carlo Histogram Image
          if (histogramImg) {
            checkSpace(60);
            doc.setFontSize(10);
            doc.setTextColor(250, 250, 250);
            doc.text('EV Distribution (Histogram)', 15, y);
            y += 4;
            doc.addImage(histogramImg, 'PNG', 15, y, pageW - 30, 50);
            y += 54;
          }

          // Percentiles
          if (monteCarlo.percentiles) {
            checkSpace(45);
            doc.setFontSize(10);
            doc.setTextColor(250, 250, 250);
            doc.text('Value at Risk (Percentiles)', 15, y);
            y += 6;

            const pctRows = [
              ['P5 (Pessimistic)', fmt(monteCarlo.percentiles.p5), 'Downside risk - 5% chance EV is below this'],
              ['P25', fmt(monteCarlo.percentiles.p25), 'Lower quartile'],
              ['P50 (Median)', fmt(monteCarlo.percentiles.p50), 'Most likely outcome'],
              ['P75', fmt(monteCarlo.percentiles.p75), 'Upper quartile'],
              ['P95 (Optimistic)', fmt(monteCarlo.percentiles.p95), 'Upside potential - 5% chance EV exceeds this'],
            ];

            autoTable(doc, {
              startY: y,
              head: [['Percentile', 'Enterprise Value', 'Interpretation']],
              body: pctRows,
              theme: 'plain',
              styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
              headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
              alternateRowStyles: { fillColor: [24, 24, 27] },
              margin: { left: 15, right: 15 },
              columnStyles: { 2: { cellWidth: 70 } },
              didDrawPage: (data) => { lastTableY = data.cursor.y; }
            });
            y = lastTableY + 8;
          }
        }
      }

      // ── Compare Deals ──
      if (selectedSections.compareDeals && manualComps.length > 0) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Comparable Deals Analysis', 15, y + 6);
        y += 14;

        // Get current deal metrics
        const sdeAvg = sde.find(s => s.label?.includes('Average'))?.sde || sde[sde.length - 1]?.sde || 0;
        const currentMetrics = {
          name: analysis?.name || 'Current Deal',
          purchase_price: dc.buyer?.purchase_price || 0,
          total_deal_size: dc.buyer?.total_deal_size || 0,
          sde: sdeAvg,
          sde_multiple: dc.buyer?.total_deal_size && sdeAvg ? dc.buyer.total_deal_size / sdeAvg : 0,
          dscr: dc.buyer?.dscr || 0,
        };

        // Build comparison table with all deals
        const allDealsData = [currentMetrics];
        manualComps.forEach(comp => {
          const price = parseFloat(comp.purchase_price) || 0;
          const compSde = parseFloat(comp.sde) || 0;
          const wc = parseFloat(comp.working_capital) || 0;
          const fees = parseFloat(comp.closing_fees) || 0;
          const totalDeal = price + wc + fees;
          allDealsData.push({
            name: comp.name || 'Unnamed Comp',
            purchase_price: price,
            total_deal_size: totalDeal,
            sde: compSde,
            sde_multiple: compSde > 0 ? totalDeal / compSde : 0,
            dscr: parseFloat(comp.dscr) || null,
          });
        });

        const compRows = allDealsData.map(d => [
          d.name,
          fmt(d.purchase_price),
          fmt(d.total_deal_size),
          fmt(d.sde),
          d.sde_multiple ? d.sde_multiple.toFixed(2) + 'x' : '—',
          d.dscr ? d.dscr.toFixed(2) + 'x' : '—',
        ]);

        autoTable(doc, {
          startY: y,
          head: [['Deal', 'Purchase Price', 'Total Deal', 'SDE', 'Multiple', 'DSCR']],
          body: compRows,
          theme: 'plain',
          styles: { fontSize: 8, textColor: [200, 200, 200], cellPadding: 3 },
          headStyles: { fillColor: [39, 39, 42], textColor: [161, 161, 170], fontStyle: 'bold' },
          alternateRowStyles: { fillColor: [24, 24, 27] },
          margin: { left: 15, right: 15 },
          didDrawPage: (data) => { lastTableY = data.cursor.y; }
        });
        y = lastTableY + 8;

        // Notes for each comp
        const compsWithNotes = manualComps.filter(c => c.notes);
        if (compsWithNotes.length > 0) {
          checkSpace(20);
          doc.setFontSize(10);
          doc.setTextColor(250, 250, 250);
          doc.text('Notes:', 15, y);
          y += 5;
          compsWithNotes.forEach(c => {
            checkSpace(8);
            doc.setFontSize(8);
            doc.setTextColor(99, 102, 241);
            doc.text(`${c.name || 'Comp'}: `, 15, y);
            doc.setTextColor(161, 161, 170);
            doc.text(c.notes, 50, y);
            y += 5;
          });
        }
      }

      // ── Key Findings ──
      if (selectedSections.findings && (flags.length > 0 || insights)) {
        addPage();
        currentSectionNum++;
        doc.setTextColor(99, 102, 241);
        doc.setFontSize(8);
        doc.text(`SECTION ${currentSectionNum}`, 15, y);
        doc.setTextColor(250, 250, 250);
        doc.setFontSize(16);
        doc.text('Key Findings & Recommendations', 15, y + 6);
        y += 14;

        // Trend flags
        if (flags.length > 0) {
          doc.setFontSize(11);
          doc.setTextColor(250, 250, 250);
          doc.text('Automated Trend Flags', 15, y); y += 6;

          flags.forEach(f => {
            checkSpace(8);
            const isPos = f.type === 'positive';
            doc.setFillColor(isPos ? 16 : 239, isPos ? 185 : 68, isPos ? 129 : 68);
            doc.circle(18, y - 1, 1.5, 'F');
            doc.setFontSize(7);
            doc.setTextColor(isPos ? 16 : 239, isPos ? 185 : 68, isPos ? 129 : 68);
            doc.text(f.type.toUpperCase(), 22, y);
            doc.setTextColor(161, 161, 170);
            doc.text(`${f.category}: ${f.message}`, 42, y);
            y += 5;
          });
          y += 5;
        }

        // Red flags section
        const negFlags = flags.filter(f => f.type === 'negative');
        if (negFlags.length > 0) {
          checkSpace(20);
          doc.setFillColor(239, 68, 68, 0.1);
          doc.roundedRect(15, y, pageW - 30, 6 + negFlags.length * 5, 2, 2, 'F');
          doc.setFontSize(9);
          doc.setTextColor(239, 68, 68);
          doc.text('Red Flags Requiring Attention:', 20, y + 5);
          y += 10;
          negFlags.forEach(f => {
            doc.setFontSize(8);
            doc.setTextColor(200, 200, 200);
            doc.text(`• ${f.message}`, 22, y);
            y += 5;
          });
          y += 5;
        }

        // AI Insights - FIXED: Handle page overflow properly
        if (insights) {
          checkSpace(20);
          doc.setFontSize(11);
          doc.setTextColor(99, 102, 241);
          doc.text('AI Deal Analysis', 15, y); y += 6;
          doc.setFontSize(8);
          doc.setTextColor(200, 200, 200);
          const lines = doc.splitTextToSize(insights, pageW - 30);
          lines.forEach(line => {
            // FIXED: checkSpace now properly fills background on new pages
            checkSpace(4.5);
            doc.text(line, 15, y);
            y += 4;
          });
        }
      }

      // Footer on all pages - FIXED: Fill background before adding footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        // Ensure dark background on footer area
        doc.setFillColor(9, 9, 11);
        doc.rect(0, pageH - 15, pageW, 15, 'F');
        doc.setFontSize(7);
        doc.setTextColor(113, 113, 122);
        doc.text('AcquiFlow | Business Acquisition Analysis Tool', 15, pageH - 5);
        doc.text(`Page ${i} of ${pageCount}  |  Confidential`, pageW - 55, pageH - 5);
      }

      doc.save(`${(analysis?.name || 'Analysis').replace(/\s+/g, '_')}_Report.pdf`);
      toast.success('PDF report generated with embedded charts');
    } catch (e) {
      console.error('PDF generation error:', e);
      toast.error('Failed to generate PDF');
    } finally {
      setGenerating(false);
    }
  }, [analysis, results, data, dc, sde, pnl, solvency, inventory, workingCapital, dcf, scenarios, sensitivity, monteCarlo, flags, insights, selectedSections]);

  const hasData = Object.keys(results || {}).length > 0;
  const selectedCount = Object.values(selectedSections).filter(Boolean).length;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="report-page">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold text-zinc-100 font-['Manrope']">Generate Report</h2>
          <p className="text-sm text-zinc-400 mt-1">Export PDF with embedded charts, findings, and AI recommendations</p>
        </div>
        <Button
          onClick={generatePDF}
          disabled={generating || !hasData || selectedCount === 0}
          className="bg-indigo-600 hover:bg-indigo-500 text-white"
          data-testid="generate-pdf-btn"
        >
          {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}
          Download PDF Report
        </Button>
      </div>

      {/* Report Contents with Checkboxes */}
      <Card className="bg-zinc-900/60 border-zinc-800/60">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-zinc-300">Report Contents</CardTitle>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={selectAll} className="text-xs text-zinc-400 hover:text-zinc-200 h-7 px-2">
                Select All
              </Button>
              <Button variant="ghost" size="sm" onClick={selectNone} className="text-xs text-zinc-400 hover:text-zinc-200 h-7 px-2">
                Clear
              </Button>
              <Badge variant="outline" className="text-[10px] border-zinc-600 text-zinc-400">
                {selectedCount} selected
              </Badge>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-xs text-zinc-500 mb-3">Check the sections you want to include in the PDF report. Table of contents and page numbers will adjust automatically.</p>
          <div className="space-y-1">
            {sectionConfig.map((item) => (
              <div key={item.key} className="flex items-center justify-between py-2.5 px-2 rounded-md hover:bg-zinc-800/40 transition-colors border-b border-zinc-800/40 last:border-0">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id={item.key}
                    checked={selectedSections[item.key]}
                    onCheckedChange={() => toggleSection(item.key)}
                    disabled={item.key === 'cover' || !item.ready}
                    className="data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600"
                    data-testid={`check-${item.key}`}
                  />
                  <label 
                    htmlFor={item.key} 
                    className={`text-sm cursor-pointer ${selectedSections[item.key] ? 'text-zinc-200' : 'text-zinc-500'}`}
                  >
                    {item.label}
                  </label>
                </div>
                <Badge variant="outline" className={`text-[10px] ${item.ready ? 'border-emerald-500/30 text-emerald-400' : 'border-zinc-600/30 text-zinc-500'}`}>
                  {item.ready ? 'Ready' : 'No Data'}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Hidden chart containers for capture */}
      {hasData && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-zinc-300 font-['Manrope']">Chart Previews (embedded in PDF)</h3>

          {pnl.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4" ref={chartRevRef}>
                <p className="text-xs text-zinc-400 mb-2">Revenue & Gross Profit</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={revenueChartData} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="year" tick={{ fill: '#71717a', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#a1a1aa' }} />
                    <Bar dataKey="Revenue" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Gross Profit" fill="#10b981" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {pnl.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4" ref={chartMarginRef}>
                <p className="text-xs text-zinc-400 mb-2">Margin Trends</p>
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={marginChartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="year" tick={{ fill: '#71717a', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `${(v * 100).toFixed(0)}%`} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#a1a1aa' }} />
                    <Line type="monotone" dataKey="Gross Margin" stroke="#6366f1" strokeWidth={2} dot={{ r: 3 }} />
                    <Line type="monotone" dataKey="Op. Margin" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3 }} />
                  </LineChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {scenarioChartData.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4" ref={chartScenarioRef}>
                <p className="text-xs text-zinc-400 mb-2">Scenario Comparison</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={scenarioChartData} barGap={8}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="name" tick={{ fill: '#71717a', fontSize: 10 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 10 }} tickFormatter={(v) => `$${(v / 1000000).toFixed(1)}M`} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 10, color: '#a1a1aa' }} />
                    <Bar dataKey="Enterprise Value" fill="#6366f1" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="Asking Price" fill="#f59e0b" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Tornado Chart */}
          {sensitivity.variables?.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4" ref={chartTornadoRef}>
                <p className="text-xs text-zinc-400 mb-2">Tornado Chart - Variable Impact on EV</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart
                    data={sensitivity.variables.map(v => ({
                      variable: v.variable,
                      low: v.ev_low - (sensitivity.base_ev || dcf.enterprise_value || 0),
                      high: v.ev_high - (sensitivity.base_ev || dcf.enterprise_value || 0),
                    }))}
                    layout="vertical"
                    barGap={-4}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis type="number" tick={{ fill: '#71717a', fontSize: 9 }} tickFormatter={(v) => `$${(v / 1000).toFixed(0)}K`} />
                    <YAxis dataKey="variable" type="category" tick={{ fill: '#71717a', fontSize: 9 }} width={100} />
                    <Tooltip formatter={(v) => fmt(v)} />
                    <Bar dataKey="low" fill="#ef4444" name="Downside" radius={[3, 0, 0, 3]} />
                    <Bar dataKey="high" fill="#10b981" name="Upside" radius={[0, 3, 3, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Monte Carlo Histogram */}
          {monteCarlo.histogram?.length > 0 && (
            <Card className="bg-zinc-900/60 border-zinc-800/60">
              <CardContent className="p-4" ref={chartHistogramRef}>
                <p className="text-xs text-zinc-400 mb-2">EV Distribution (Monte Carlo Histogram)</p>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={monteCarlo.histogram}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                    <XAxis dataKey="label" tick={{ fill: '#71717a', fontSize: 9 }} />
                    <YAxis tick={{ fill: '#71717a', fontSize: 9 }} />
                    <Tooltip 
                      formatter={(v, name) => [v, 'Count']}
                      labelFormatter={(label) => `Range: ${label}`}
                    />
                    <Bar dataKey="count" fill="#6366f1" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {!hasData && (
        <Card className="bg-zinc-900/40 border-zinc-800/40">
          <CardContent className="p-8 text-center">
            <BarChart3 className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
            <p className="text-sm text-zinc-400">Import financial data first to generate a report.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
